import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  query,
  collection,
  where,
  runTransaction,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from "firebase/firestore";
import {
  Card,
  Table,
  Button,
  Alert,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";

/**
 * MembersPanel — Manager/Admin view
 *
 * Props:
 *  - communityId: string (required)
 *  - canEditRole?: boolean (default false)
 *  - onEditRole?: (uid: string) => void (default no-op)
 *  - isManagerOrAdmin?: boolean (default false)  // controls Delete visibility
 *
 * Shows:
 *  - Current members (community.members enriched with user profiles),
 *  - Pending users (users where unit.communityId == communityId OR legacy communityId == communityId AND status == 'pending'),
 * Actions:
 *  - Approve: add uid to community.members + set users/{uid}.status='approved' (+ audit)
 *  - Reject:  set users/{uid}.status='rejected' (+ audit)
 *  - Delete:  remove uid from community.members (+ audit, with reason), only for managers/admins
 *  - Undo:    restore prior state (status, unit, communities) and re-add to community.members (+ audit)
 */
export default function MembersPanel({
  communityId,
  canEditRole = false,
  onEditRole = () => {},
  isManagerOrAdmin = false,
}) {
  // Approved member UIDs & details
  const [memberUids, setMemberUids] = useState([]);
  const [memberDetails, setMemberDetails] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState("");

  // Pending users
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingError, setPendingError] = useState("");

  // Success feedback + undo state
  const [successMsg, setSuccessMsg] = useState("");
  const [lastDeleted, setLastDeleted] = useState(null); // { uid, name, communityId }

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null); // full user object from memberDetails
  const [deleteReason, setDeleteReason] = useState("");

  // ----- Subscribe to community doc: get members array -----
  useEffect(() => {
    if (!communityId) return;
    setMembersLoading(true);
    setMembersError("");

    const ref = doc(db, "communities", communityId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setMembersError("Community not found.");
          setMemberUids([]);
          setMembersLoading(false);
          return;
        }
        const data = snap.data();
        const uids = Array.isArray(data.members) ? data.members : [];
        setMemberUids(uids);
        setMembersLoading(false);
      },
      (err) => {
        console.error("onSnapshot members error:", err.code, err.message);
        setMembersError(`${err.code || ""} ${err.message || ""}`.trim() || "Failed to load members.");
        setMembersLoading(false);
      }
    );

    return () => unsub();
  }, [communityId]);

  // ----- Enrich members with user profiles -----
  useEffect(() => {
    let cancelled = false;

    const loadProfiles = async () => {
      if (!memberUids.length) {
        setMemberDetails([]);
        return;
      }
      const snaps = await Promise.all(
        memberUids.map((uid) => getDoc(doc(db, "users", uid)))
      );
      if (cancelled) return;
      const rows = snaps.map((s) =>
        s.exists()
          ? { uid: s.id, ...s.data() }
          : { uid: s.id, fullName: "—", email: "—", mobile: "—", role: "—" }
      );
      setMemberDetails(rows);
    };

    loadProfiles().catch((err) => console.error("Load member profiles error:", err));
    return () => {
      cancelled = true;
    };
  }, [memberUids]);

  // ----- Subscribe to pending users (new + legacy schemas) -----
  useEffect(() => {
    if (!communityId) return;
    setPendingLoading(true);
    setPendingError("");

    const qA = query(
      collection(db, "users"),
      where("unit.communityId", "==", communityId),
      where("status", "==", "pending")
    );

    const qB = query(
      collection(db, "users"),
      where("communityId", "==", communityId),
      where("status", "==", "pending")
    );

    const unsubs = [];
    let lastA = [], lastB = [];

    const apply = (docsA, docsB) => {
      const map = new Map();
      [...docsA, ...docsB].forEach((d) => map.set(d.id, { id: d.id, uid: d.id, ...d.data() }));
      setPending([...map.values()]);
      setPendingLoading(false);
    };

    const handleErr = (err) => {
      console.error("onSnapshot pending error:", err.code, err.message);
      setPendingError(`${err.code || ""} ${err.message || ""}`.trim() || "Failed to load pending users.");
      setPendingLoading(false);
    };

    unsubs.push(
      onSnapshot(qA, (snap) => {
        lastA = snap.docs;
        apply(lastA, lastB);
      }, handleErr)
    );
    unsubs.push(
      onSnapshot(qB, (snap) => {
        lastB = snap.docs;
        apply(lastA, lastB);
      }, handleErr)
    );

    return () => unsubs.forEach((u) => u());
  }, [communityId]);

  // ----- Approve action -----
  const approve = async (user) => {
    const auth = getAuth();
    const managerUid = auth.currentUser?.uid || null;
    const applicantUid = user.uid;

    if (!applicantUid) {
      alert("Invalid user record (missing uid).");
      return;
    }

    try {
      const communityRef = doc(db, "communities", communityId);
      const userRef = doc(db, "users", applicantUid);

      await runTransaction(db, async (t) => {
        // READS
        const cSnap = await t.get(communityRef);
        if (!cSnap.exists()) throw new Error("Community not found.");

        const uSnap = await t.get(userRef);
        if (!uSnap.exists()) throw new Error("User not found.");
        const uData = uSnap.data();

        if (uData.status !== "pending") {
          throw new Error("User is not in pending status.");
        }

        const userCommunityId = uData?.unit?.communityId ?? uData?.communityId;
        if (userCommunityId && userCommunityId !== communityId) {
          throw new Error("User does not belong to this community.");
        }

        // WRITES
        t.update(communityRef, { members: arrayUnion(applicantUid) });
        t.update(userRef, {
          status: "approved",
          "audit.approvedAt": serverTimestamp(),
          "audit.approvedByUid": managerUid,
        });
      });
    } catch (err) {
      console.error("Approve error:", err);
      alert(err?.message || "Failed to approve user.");
    }
  };

  // ----- Reject action -----
  const reject = async (user) => {
    const auth = getAuth();
    const managerUid = auth.currentUser?.uid || null;
    const applicantUid = user.uid;

    if (!applicantUid) {
      alert("Invalid user record (missing uid).");
      return;
    }

    try {
      const userRef = doc(db, "users", applicantUid);
      await updateDoc(userRef, {
        status: "rejected",
        "audit.rejectedAt": serverTimestamp(),
        "audit.rejectedByUid": managerUid,
      });
    } catch (err) {
      console.error("Reject error:", err);
      alert(err?.message || "Failed to reject user.");
    }
  };

  // ----- Open Delete modal (with reason input) -----
  const openDeleteModalFor = (user) => {
    if (!isManagerOrAdmin) {
      alert("Not allowed.");
      return;
    }
    setDeleteTargetUser(user);
    setDeleteReason("");
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTargetUser(null);
    setDeleteReason("");
  };

  // ----- Delete member (requires reason) -----
  const confirmDeleteMember = async () => {
    if (!deleteTargetUser?.uid) return;
    if (!deleteReason.trim()) {
      alert("Please enter a reason for deletion.");
      return;
    }

    const auth = getAuth();
    const managerUid = auth.currentUser?.uid || null;
    const memberUid = deleteTargetUser.uid;
    const nameLbl =
      deleteTargetUser?.fullName ||
      deleteTargetUser?.displayName ||
      memberUid;

    try {
      const communityRef = doc(db, "communities", communityId);
      const userRef = doc(db, "users", memberUid);

      await runTransaction(db, async (t) => {
        // READS
        const cSnap = await t.get(communityRef);
        if (!cSnap.exists()) throw new Error("Community not found.");
        const cData = cSnap.data();
        const currentMembers = Array.isArray(cData.members) ? cData.members : [];

        const uSnap = await t.get(userRef);
        if (!uSnap.exists()) throw new Error("User not found.");
        const uData = uSnap.data();

        const prevCommunities = Array.isArray(uData.communities) ? uData.communities : [];
        const prevUnit = uData.unit || null;
        const prevStatus = uData.status || "approved";

        // WRITES
        // 1) Remove from community.members (arrayRemove is atomic)
        if (currentMembers.includes(memberUid)) {
          t.update(communityRef, { members: arrayRemove(memberUid) });
        }

        // 2) Update user doc: remove communityId from communities[], clear unit.communityId if matches
        const nextCommunities = prevCommunities.filter((cid) => cid !== communityId);
        const nextUnit =
          prevUnit?.communityId === communityId
            ? { ...prevUnit, communityId: null }
            : prevUnit;

        // Audit trail: persist detailed snapshot for undo
        t.update(userRef, {
          communities: nextCommunities,
          unit: nextUnit,
          status: prevStatus === "approved" ? "removed" : prevStatus, // optional marker
          "audit.removedAt": serverTimestamp(),
          "audit.removedByUid": managerUid,
          "audit.removedFromCommunityId": communityId,
          "audit.removeReason": deleteReason.trim(),
          "audit.prevBeforeRemoval": {
            status: prevStatus,
            unit: prevUnit || null,
            communities: prevCommunities,
          },
        });
      });

      // Success feedback + allow undo
      setSuccessMsg(`Member "${nameLbl}" was removed from the community.`);
      setLastDeleted({
        uid: memberUid,
        name: nameLbl,
        communityId,
      });
      closeDeleteModal();
    } catch (err) {
      console.error("Delete member error:", err);
      alert(err?.message || "Failed to delete member.");
    }
  };

  // ----- Undo delete -----
  const undoDelete = async () => {
    if (!lastDeleted?.uid) return;

    const auth = getAuth();
    const managerUid = auth.currentUser?.uid || null;
    const memberUid = lastDeleted.uid;

    try {
      const communityRef = doc(db, "communities", communityId);
      const userRef = doc(db, "users", memberUid);

      await runTransaction(db, async (t) => {
        // READS
        const uSnap = await t.get(userRef);
        if (!uSnap.exists()) throw new Error("User not found for undo.");
        const uData = uSnap.data();

        // We must have persisted prev snapshot in audit.prevBeforeRemoval
        const prev = uData?.audit?.prevBeforeRemoval || null;
        if (!prev) {
          throw new Error("No previous snapshot found to undo removal.");
        }
        const prevStatus = prev.status || "approved";
        const prevUnit = prev.unit || null;
        const prevCommunities = Array.isArray(prev.communities) ? prev.communities : [];

        // READ community doc to avoid duplicate
        const cSnap = await t.get(communityRef);
        if (!cSnap.exists()) throw new Error("Community not found.");
        const cData = cSnap.data();
        const currentMembers = Array.isArray(cData.members) ? cData.members : [];

        // WRITES
        // 1) Re-add to community.members
        if (!currentMembers.includes(memberUid)) {
          t.update(communityRef, { members: arrayUnion(memberUid) });
        }

        // 2) Restore user doc fields + audit
        t.update(userRef, {
          communities: prevCommunities,
          unit: prevUnit,
          status: prevStatus,
          "audit.undoRemovalAt": serverTimestamp(),
          "audit.undoRemovalByUid": managerUid,
          // Optional: keep the removal record, but mark a lastAction
          "audit.lastAction": "undo-removal",
        });
      });

      setSuccessMsg(`Member "${lastDeleted.name}" was restored.`);
      setLastDeleted(null);
    } catch (err) {
      console.error("Undo delete error:", err);
      alert(err?.message || "Failed to undo removal.");
    }
  };

  return (
    <Card>
      <Card.Body>
        {/* Success message with Undo action */}
        {successMsg && (
          <Alert variant="success" className="mb-3 d-flex justify-content-between align-items-center">
            <div>{successMsg}</div>
            {lastDeleted?.uid && (
              <div className="d-flex gap-2">
                <Button variant="outline-success" size="sm" onClick={undoDelete}>
                  Undo
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setSuccessMsg("");
                    setLastDeleted(null);
                  }}
                >
                  Dismiss
                </Button>
              </div>
            )}
          </Alert>
        )}

        {/* Approved members */}
        <h5 className="mb-3">Members</h5>

        {membersError && <Alert variant="danger">{membersError}</Alert>}

        {membersLoading ? (
          <div className="d-flex align-items-center mb-3">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>Loading members…</span>
          </div>
        ) : (
          <Table bordered hover responsive size="sm" className="mb-4">
            <thead>
              <tr>
                <th>Name</th>
                <th>Block</th>
                <th>FlatNo</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Role </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {memberDetails.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No members
                  </td>
                </tr>
              ) : (
                memberDetails.map((u) => (
                  <tr key={u.uid}>
                    <td>{u.fullName || "—"}</td>
                    <td>{u.unit?.blockName || "—"}</td>
                    <td>{u.unit?.flatLabel || "—"}</td>
                    <td>{u.email || "—"}</td>
                    <td>{u.mobile || "—"}</td>
                    <td className="d-flex align-items-center gap-2">
                      {/* Role display or edit trigger */}
                      {canEditRole ? (
                        <p
                          size="sm"
                          variant="outline-primary"
                          onClick={() => onEditRole(u.uid)}
                        >
                          {u.role || "—"}
                        </p>
                      ) : (
                        <span>{u.role || "—"}</span>
                      )}
                      </td>

                    <td>  {/* Delete beside role, only for Manager/Admin */}
                      {canEditRole && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => openDeleteModalFor(u)}
                          title="Remove member from community"
                        >
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {/* Pending approvals */}
        <h5 className="mb-3">Pending Approvals</h5>

        {pendingError && <Alert variant="danger">{pendingError}</Alert>}

        {pendingLoading ? (
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>Loading pending users…</span>
          </div>
        ) : (
          <Table bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Block</th>
                <th>Flat</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No pending requests
                  </td>
                </tr>
              ) : (
                pending.map((u) => (
                  <tr key={u.uid}>
                    <td>{u.fullName || "—"}</td>
                    <td>{u.email || "—"}</td>
                    <td>{u.mobile || "—"}</td>
                    <td>{u.unit?.blockName || "—"}</td>
                    <td>{u.unit?.flatLabel || "—"}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => approve(u)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => reject(u)}
                        >
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {/* Delete Reason Modal */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Member</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            You are removing{" "}
            <strong>
              {deleteTargetUser?.fullName ||
                deleteTargetUser?.displayName ||
                deleteTargetUser?.uid ||
                "Member"}
            </strong>{" "}
            from this community.
          </p>
          <Form.Group className="mt-2">
            <Form.Label>Reason for removal (required)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="e.g., moved out, duplicate record, incorrect assignment…"
            />
            <Form.Text className="text-muted">
              This reason will be stored in the audit trail.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteMember}>
            Confirm Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
